import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { ResourceType } from 'js/social/shared/types';
import useConfirmationModal from 'js/social/shared/ConfirmationModal/useConfirmationModal';
import startCase from 'lodash/startCase';
import styled from 'astroturf/react';
import { ContentFeedback } from 'js/social/shared/ContentFeedback';

// How many lines tall the textarea should be when opening the modal.
const DEFAULT_ROWS = 5;

const Textarea = styled.textarea`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  border: 0;
  color: #000;
  margin-top: 16px;
  margin-bottom: 16px;
  resize: vertical;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

// Used to manage actions for header menu
const useMenuActions = (
  userDisplayName: string,
  itemType: ResourceType,
  isModalOpened: boolean,
  modal: Pick<
    ReturnType<typeof useConfirmationModal>,
    'openModal' | 'closeModal' | 'setDisableAction'
  >,
  actions: {
    followUser: () => Promise<void>;
    unfollowUser: () => Promise<void>;
    blockUser: () => void;
    ignoreItem: (feedbackType: number, reason: number) => void;
    reportItem: (reason?: string) => void;
    deleteItem: () => void;
    removeTag: () => void;
  },
) => {
  const [reportReason, setReportReason] = useState('');
  const [ignoreReason, setIgnoreReason] = useState<number>();
  const reportTextareaRef = useRef<HTMLTextAreaElement>(null);

  const reportItem = useCallback(() => {
    modal.setDisableAction(true);
    new Promise<void>((resolve) => {
      if (!reportReason.trim()) {
        actions.reportItem();
      } else {
        actions.reportItem(reportReason);
      }
      resolve();
    })
      .then(modal.closeModal)
      .finally(() => modal.setDisableAction(false));
  }, [actions, modal, reportReason]);

  const itemTypeForUser = useMemo(() => {
    return (itemType === 'paper_update' ? 'post' : itemType).replace('_', ' ');
  }, [itemType]);

  const reportReasonTextarea = useMemo(() => {
    return (
      <Textarea
        ref={reportTextareaRef}
        rows={DEFAULT_ROWS}
        spellCheck="true"
        value={reportReason}
        placeholder="What is the problem with this content?"
        onChange={(e) => setReportReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.metaKey && e.key === 'Enter') {
            reportItem();
          }
        }}
      />
    );
  }, [reportReason, reportItem]);

  const followUser = async () => {
    await actions.followUser();
  };

  const openUnfollowUserConfirmationModal = () => {
    modal.openModal(
      `Unfollow ${userDisplayName}`,
      `Are you sure you want to unfollow ${userDisplayName}?`,
      'Unfollow',
      () => {
        modal.setDisableAction(true);
        actions
          .unfollowUser()
          .then(() => {
            modal.closeModal();
          })
          .finally(() => modal.setDisableAction(false));
      },
    );
  };

  const openBlockUserConfirmationModal = () => {
    modal.openModal(
      `Block ${userDisplayName}`,
      `Are you sure you want to block ${userDisplayName}?`,
      'Block',
      () => {
        modal.setDisableAction(true);
        new Promise((resolve) => {
          actions.blockUser();
          resolve(() => {});
        })
          .then(() => {
            modal.closeModal();
          })
          .finally(() => modal.setDisableAction(false));
      },
    );
  };

  const openReportItemConfirmationModal = useCallback(() => {
    modal.setDisableAction(false);
    modal.openModal(
      `Report ${startCase(itemTypeForUser)}`,
      reportReasonTextarea,
      'Report',
      reportItem,
    );

    // Make sure the input is focused
    setTimeout(() => reportTextareaRef.current?.focus(), 0);
  }, [modal, itemTypeForUser, reportItem, reportReasonTextarea]);

  const openDeleteItemConfirmationModal = () => {
    modal.openModal(
      `Delete ${startCase(itemTypeForUser)}`,
      `Are you sure you want to delete this ${itemTypeForUser}?`,
      'Delete',
      () => {
        modal.setDisableAction(true);
        new Promise((resolve) => {
          actions.deleteItem();
          resolve(() => {});
        })
          .then(() => {
            modal.closeModal();
          })
          .finally(() => modal.setDisableAction(false));
      },
    );
  };

  const openUntagItemConfirmationModal = () => {
    modal.openModal(
      `Remove Tag`,
      `Are you sure you want to remove this tag? This can't be undone.`,
      'Remove tag',
      () => {
        modal.setDisableAction(true);
        new Promise((resolve) => {
          actions.removeTag();
          resolve(() => {});
        })
          .then(() => {
            modal.closeModal();
          })
          .finally(() => modal.setDisableAction(false));
      },
    );
  };

  const ignoreItem = () => {
    modal.setDisableAction(true);
    new Promise<void>((resolve, reject) => {
      if (ignoreReason === undefined) {
        reject();
      } else {
        actions.ignoreItem(0, ignoreReason);
      }
      ignoreReason === undefined ? reject() : resolve();
    })
      .then(modal.closeModal)
      .finally(() => modal.setDisableAction(false));
  };

  const ignoreInput = useMemo(() => {
    const reasons = [
      { id: 0, name: "I'm not interested in this topic" },
      { id: 1, name: "I don't like this author" },
      { id: 2, name: "I've already seen this" },
      { id: 3, name: 'Something else' },
    ];

    return (
      <ContentFeedback
        reasonId={ignoreReason}
        reasons={reasons}
        onChange={(reasonId) => {
          modal.setDisableAction(false);
          setIgnoreReason(reasonId);
        }}
        onReport={openReportItemConfirmationModal}
      />
    );
  }, [modal, ignoreReason, openReportItemConfirmationModal]);

  const openIgnoreItemConfirmationModal = (initial = true) => {
    if (initial) {
      setIgnoreReason(undefined);
      modal.setDisableAction(true);
    }
    modal.openModal(undefined, ignoreInput, 'Submit', ignoreItem);
  };

  // TODO: Fix this strange state management by moving modals to individual components

  const reportUpdateRef = useRef(false);
  useEffect(() => {
    if (reportUpdateRef.current) {
      openReportItemConfirmationModal();
    }
    reportUpdateRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportReason]);

  const ignoreUpdateRef = useRef(false);
  useEffect(() => {
    if (ignoreUpdateRef.current) {
      openIgnoreItemConfirmationModal(false);
    }
    ignoreUpdateRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreReason]);

  return {
    followUser,
    openUnfollowUserConfirmationModal,
    openBlockUserConfirmationModal,
    openReportItemConfirmationModal,
    openIgnoreItemConfirmationModal,
    openDeleteItemConfirmationModal,
    openUntagItemConfirmationModal,
  };
};

export default useMenuActions;
